// Survey-type sub-services for the Land Surveying CAD Drafting Drafting page.
// Content adapted from landdrafters.com/services/land-surveying/* subpages.
// Each entry powers a card on the main page AND a full sub-page detail view.

export const SURVEY_TYPES = [
  {
    slug: 'alta-surveys',
    title: 'ALTA/NSPS Land Title Surveys',
    short: 'CAD drafting for ALTA/NSPS commercial title surveys — produced from your field data, ready for title insurance and closings.',
    icon: 'FileCheck2',
    image: 'https://images.unsplash.com/photo-1628155858736-bacadae33a88?w=1200&h=800&fit=crop',
    features: [
      { title: 'Table A Drafting',     desc: 'All 21 Table A items drafted to selection' },
      { title: 'Title-Ready Exhibits', desc: 'Closing & lender-grade deliverables' },
      { title: 'CAD Drafting Only',    desc: 'No field work — from your data' },
      { title: '24–48hr Turnaround',   desc: 'Fast closing-driven delivery' }
    ],
    overview: 'Land Drafters LLC provides outsourced CAD drafting services for ALTA/NSPS Land Title Surveys. Working from the field data, deed research, and Table A selections supplied by your licensed surveyor, our drafting team produces commercial-grade ALTA exhibits ready for title insurance, closings, and lender review. We do not perform field surveys — your crew measures, we draft.',
    deliverables: [
      'Boundary linework & closure drafting',
      'Easement plotting from recorded documents',
      'Encroachment & improvement layering',
      'Table A optional items drafted to selection',
      'Surveyor notes, certifications, & title block',
      'Zoning, flood zone, & utility annotation'
    ],
    faqs: [
      { q: 'Do you perform the ALTA field survey?', a: 'No. We are a CAD drafting partner. Your licensed surveyor performs the field work and seals the deliverable — we produce the drawing.' },
      { q: 'Which surveyors do you work with?',     a: 'We work exclusively with licensed PLS firms and engineers in the US. We never deliver direct to property owners or lenders.' },
      { q: 'What Table A items can you draft?',     a: 'All 21 current Table A items — from zoning classification and flood zones to off-site easements and utility evidence — drafted to your specifications.' },
      { q: 'How long does the drafting take?',      a: 'Standard ALTA exhibits are drafted in 24–48 hours after we receive your field data and Table A selections. Rush turnaround is available.' }
    ]
  },
  {
    slug: 'boundary-surveys',
    title: 'Boundary Surveys',
    short: 'CAD drafting for boundary surveys — clean linework, monument calls, and legal descriptions from your field notes.',
    icon: 'Frame',
    image: 'https://images.unsplash.com/photo-1545558014-8692077e9b5c?w=1200&h=800&fit=crop',
    features: [
      { title: 'Boundary Linework', desc: 'Drafted from your field data' },
      { title: 'Metes & Bounds',    desc: 'Legal descriptions drafted' },
      { title: 'Plat-Ready Files',  desc: 'Layered DWG / DXF / PDF' },
      { title: 'White-Label',       desc: 'Confidential — ready for your seal' }
    ],
    overview: 'Our boundary survey CAD drafting service converts your field-collected data — corner points, monument descriptions, deed calls, and historical records — into clean, layered boundary exhibits. We support licensed surveyors with overflow drafting capacity so your crews stay focused on field work. We do not perform field surveys.',
    deliverables: [
      'Corner & monument drafting from your field data',
      'Deed call traverse and closure adjustment',
      'Linework, bearings, & distances to your standards',
      'Metes & bounds legal description drafting',
      'Encroachment & adjoiner labeling',
      'Final sealed-ready DWG, DXF, & PDF'
    ],
    faqs: [
      { q: 'Do you set monuments or perform the field work?', a: 'Never. We are a drafting back-office. Field work — including setting and locating monuments — stays with your licensed surveyor.' },
      { q: 'What field data do you need from us?',           a: 'Point files (PNEZD / ASCII), raw data, field notes, sketches, prior surveys, deeds, and any required templates or layer standards.' },
      { q: 'Can you match our firm’s CAD standards?',        a: 'Yes — share your DWG template, layer standards, and any custom blocks/title-block, and we will draft to match.' },
      { q: 'What is the turnaround time?',                   a: 'Standard residential and small-tract boundary drafts are returned in 24–48 hours. Complex tracts may take longer; we confirm timing in the quote.' }
    ]
  },
  {
    slug: 'topographic-surveys',
    title: 'Topographic Mapping',
    short: 'CAD drafting for topographic surveys — contours, breaklines, and surface modeling from your point files.',
    icon: 'Mountain',
    image: 'https://images.unsplash.com/photo-1515259387710-51e175f9ec6d?w=1200&h=800&fit=crop',
    features: [
      { title: 'Contour Drafting',  desc: '0.5ft / 1ft / 2ft intervals' },
      { title: 'Surface Modeling',  desc: 'Civil 3D TIN / DTM surfaces' },
      { title: 'From Your Points',  desc: 'PNEZD, ASCII, LiDAR processed' },
      { title: 'Base Maps',         desc: 'Ready for civil & arch design' }
    ],
    overview: 'We provide CAD drafting services for topographic mapping projects across the US. Working from the point files, breaklines, and feature codes captured by your field crew (or UAV/LiDAR data), our drafters produce contour maps, TIN surfaces, and base maps ready for civil design, architecture, and BIM. We do not perform topographic field surveys.',
    deliverables: [
      'Point file processing & feature coding',
      'Contour generation (1ft, 2ft, custom intervals)',
      'TIN / DTM surface modeling in Civil 3D',
      'Breakline & boundary integration',
      'Existing feature linework (curbs, utilities, trees)',
      'Survey base map ready for civil/architectural design'
    ],
    faqs: [
      { q: 'Do you fly drones or operate total stations?',      a: 'No. We only draft from data your crew collects. We can process UAV/LiDAR output that you provide, but we do not operate the equipment.' },
      { q: 'What contour intervals do you draft?',              a: 'Any interval — 0.5 ft, 1 ft, 2 ft, 5 ft or metric equivalents. Tell us your specification and we draft to it.' },
      { q: 'Can you build Civil 3D surfaces from our point data?', a: 'Yes — TIN surfaces with breaklines, hide/show boundaries, and styled contours, fully ready for further civil design.' },
      { q: 'What file formats do you deliver?',                 a: 'DWG, DXF, PDF, and Civil 3D project files. Earth-formats and shapefiles available on request.' }
    ]
  },
  {
    slug: 'mortgage-location-surveys',
    title: 'Mortgage Location Surveys',
    short: 'CAD drafting for residential mortgage location surveys — closing-ready exhibits delivered fast.',
    icon: 'KeyRound',
    image: 'https://images.unsplash.com/photo-1721244654394-36a7bc2da288?w=1200&h=800&fit=crop',
    features: [
      { title: 'MLC Drafts',     desc: 'Mortgage Location Certificates' },
      { title: 'Closing-Ready',  desc: 'Lender & title package format' },
      { title: 'CAD Drafting Only', desc: 'No field work — from your locates' },
      { title: '24hr Standard',  desc: 'Closing-driven turnaround' }
    ],
    overview: 'We draft Mortgage Location Certificates (MLCs) and residential closing exhibits from the field-measured improvement locates your crew provides. Quick, lender-ready, and confidential — designed to keep residential closings on schedule.',
    deliverables: [
      'Improvement location plan',
      'Building setback labels & encroachment notes',
      'Easement & utility plotting',
      'Fence, drive, and accessory structure layering',
      'Lender / title certification block',
      'Closing-package PDF + DWG'
    ],
    faqs: [
      { q: 'Is an MLC the same as a boundary survey?',  a: 'No. An MLC documents improvement locations relative to apparent boundaries but is not a full boundary survey. We draft to your firm’s MLC standards.' },
      { q: 'Can you turn around same-day closings?',    a: 'Yes — rush 4–8 hour turnaround is available on simple residential MLCs when field data and templates are provided.' },
      { q: 'Do you stamp the drawing?',                 a: 'No. Stamps and certifications are applied by your licensed surveyor; we deliver a sealed-ready exhibit.' },
      { q: 'What if encroachments are found?',          a: 'We layer encroachments with clear call-outs so your surveyor can certify or recommend further action before closing.' }
    ]
  },
  {
    slug: 'as-built-surveys',
    title: 'As-Built Surveys',
    short: 'CAD drafting for as-built surveys — post-construction overlays and compliance documentation from your field data.',
    icon: 'Construction',
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&h=800&fit=crop',
    features: [
      { title: 'As-Built Drafting', desc: 'Post-construction CAD overlays' },
      { title: 'Plan vs. Built',    desc: 'Deviation call-outs & redlines' },
      { title: 'CAD Drafting Only', desc: 'No field work — from your locates' },
      { title: '48hr Standard',     desc: 'Rapid record-drawing delivery' }
    ],
    overview: 'We provide CAD drafting services for as-built (record drawing) surveys. Working from the field data your crew captures after construction, our drafters produce overlays of constructed conditions against design plans — highlighting deviations for certificate-of-occupancy, lender disbursement, and project close-out. We do not perform the field as-built survey.',
    deliverables: [
      'Building footprint & finished floor elevation drafting',
      'Utility as-built mapping from field locates',
      'Pavement, drainage, & site improvement plotting',
      'Overlay of as-built vs. approved plans',
      'Deviation call-outs & redline reports',
      'Final plat / record drawing preparation'
    ],
    faqs: [
      { q: 'Do you perform the field as-built survey?', a: 'No. Field locates are performed by your crew or contractor. We draft the deliverable from the data you supply.' },
      { q: 'When is an as-built drawing required?',     a: 'Typically at substantial completion — for certificate-of-occupancy, lender disbursement, utility acceptance, and project close-out.' },
      { q: 'Can you overlay as-built data onto the original plans?', a: 'Yes — we layer as-built versus design and produce deviation reports flagged in color for fast review.' },
      { q: 'What deliverables do you provide?',         a: 'Layered DWG, DXF, sealed-ready PDF record drawing, and an optional deviation summary report.' }
    ]
  },
  {
    slug: 'right-of-way-surveys',
    title: 'Right-of-Way Surveys',
    short: 'CAD drafting for ROW and route surveys — alignment sheets, parcel plats, and easements for utility & transportation corridors.',
    icon: 'Route',
    image: 'https://images.unsplash.com/photo-1545558014-8692077e9b5c?w=1200&h=800&fit=crop',
    features: [
      { title: 'Alignment Sheets', desc: 'Plan & profile corridor exhibits' },
      { title: 'Parcel Plats',     desc: 'Take, residual, & dedication plats' },
      { title: 'CAD Drafting Only', desc: 'No field work — from your data' },
      { title: '48hr Standard',    desc: 'Per-station drafting turnaround' }
    ],
    overview: 'We draft right-of-way (ROW) and route survey CAD packages from the field data and historical research your team provides — alignment sheets, parcel-take plats, residual exhibits, and easements for utility, highway, rail, and pipeline corridors.',
    deliverables: [
      'ROW alignment sheets with stationing',
      'Centerline & baseline geometry',
      'Parcel take, residual, & dedication exhibits',
      'Permanent / temporary easement plotting',
      'Pre-acquisition & post-acquisition descriptions',
      'Title exhibits for acquisition agreements'
    ],
    faqs: [
      { q: 'Do you perform the field route survey?',   a: 'No. Field work, monumentation, and certification stay with your licensed surveyor. We draft the CAD package from your data.' },
      { q: 'Which agencies do you draft for?',         a: 'We have drafted to DOT, FERC, FHWA, and municipal ROW standards. Share your template and standards and we will match.' },
      { q: 'Can you draft per-station alignment sheets?', a: 'Yes — plan & profile sheets at any station interval (500′, 1,000′, 1+00, etc.) with custom title-block and match-lines.' },
      { q: 'What deliverables do I get?',              a: 'Layered DWG, sealed-ready PDFs, parcel exhibits, and Word/PDF metes-and-bounds descriptions as needed.' }
    ]
  },
  {
    slug: 'tower-surveys',
    title: 'Tower Surveys',
    short: 'CAD drafting for telecom and utility tower site surveys — compound, lease area, and access mapping.',
    icon: 'RadioTower',
    image: 'https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=1200&h=800&fit=crop',
    features: [
      { title: 'Compound Plots', desc: 'Lease area & equipment layout' },
      { title: 'FAA 1A / 2C',    desc: 'NEPA & airspace exhibits' },
      { title: 'CAD Drafting Only', desc: 'No field work — from your locates' },
      { title: 'Carrier-Ready',  desc: 'AT&T, Verizon, T-Mobile templates' }
    ],
    overview: 'We draft telecom and utility tower site CAD packages from your field-collected data — compound layout, lease area, ingress/egress, easement, and FAA airspace exhibits. We work to major carrier templates and FAA/FCC submission standards.',
    deliverables: [
      'Compound site plan & equipment layout',
      'Lease area & ingress/egress mapping',
      'FAA 1A & 2C airspace exhibits',
      'Access road & utility easement plotting',
      'Tower elevation & antenna mount views',
      'Photo-sim base & visual impact exhibits'
    ],
    faqs: [
      { q: 'Do you go to the tower site?',           a: 'Never. We draft from the field data, locates, and photos your A&E or surveyor provides.' },
      { q: 'Which carrier templates do you support?', a: 'AT&T, Verizon, T-Mobile, DISH, Crown Castle, American Tower, SBA. Send us the template and we draft to it.' },
      { q: 'Can you draft FAA 1A and 2C exhibits?',  a: 'Yes — including NEPA review packages, airspace impact mapping, and FCC tower-registration exhibits.' },
      { q: 'What turnaround can I expect?',          a: 'Standard tower CAD packages return in 24–72 hours depending on carrier template and exhibit count.' }
    ]
  },
  {
    slug: 'pipeline-surveys',
    title: 'Pipeline Surveys',
    short: 'CAD drafting for oil, gas, water and sewer pipeline routes — plan & profiles, alignment sheets, and crossings.',
    icon: 'Waves',
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1200&h=800&fit=crop',
    features: [
      { title: 'Plan & Profile', desc: 'Per-station alignment sheets' },
      { title: 'Crossings',      desc: 'Road, rail, stream & wetland' },
      { title: 'CAD Drafting Only', desc: 'No field work — from your data' },
      { title: 'HCA & HDD',      desc: 'High-consequence area mapping' }
    ],
    overview: 'We draft plan and profile sheets, alignment sheets, and crossing exhibits for oil, gas, water, and sewer pipelines from your route-survey field data. Our team supports owner operators and consulting engineers with overflow drafting capacity for short and long-haul projects.',
    deliverables: [
      'Plan & profile sheets with stationing',
      'Centerline alignment & geometry',
      'Crossing details (road / rail / stream / utility)',
      'HCA & HDD documentation exhibits',
      'Pipeline tabulation & cover-depth report',
      'As-built tie sheets & route maps'
    ],
    faqs: [
      { q: 'Do you walk pipeline ROWs?',                 a: 'No. We draft from your survey data — total-station, GNSS, UAV, or LiDAR — and from photos / sketches you supply.' },
      { q: 'What regulatory exhibits can you draft?',    a: 'PHMSA HCA, FERC, USACE 404, state-DOT crossing permits, and railway crossing exhibits — to the agency template.' },
      { q: 'Can you produce as-built tie sheets?',       a: 'Yes — line-of-sight ties, depth-of-cover logs, fitting schedules, and welding maps from your field data.' },
      { q: 'Do you support metric stationing?',          a: 'Yes — imperial or metric, your specification. Project units are confirmed at quoting.' }
    ]
  },
  {
    slug: 'subdivision-surveys',
    title: 'Subdivision Plats',
    short: 'CAD drafting of recording-ready subdivision plats — lot layouts, easements, and legal descriptions.',
    icon: 'LayoutGrid',
    image: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&h=800&fit=crop',
    features: [
      { title: 'Recording-Ready', desc: 'Local jurisdiction templates' },
      { title: 'Lot Layouts',     desc: 'Standard or custom geometry' },
      { title: 'CAD Drafting Only', desc: 'No field work — from your data' },
      { title: 'Legal Descriptions', desc: 'Metes & bounds drafted' }
    ],
    overview: 'We draft recording-ready subdivision plats from the boundary, topo and preliminary design data your team supplies — lot layouts, easements, legal descriptions, and dedication sheets formatted to your local recorder’s requirements.',
    deliverables: [
      'Lot & block layouts with bearings/distances',
      'Easement & ROW dedication mapping',
      'Curve, line, and lot tables',
      'Metes & bounds legal descriptions',
      'Plat-cover sheet, notes & signature block',
      'Recorder-ready signed/sealed PDF'
    ],
    faqs: [
      { q: 'Can you draft to my county recorder template?', a: 'Yes — send the template and any required notes, dedication blocks, and signature panels. We will match exactly.' },
      { q: 'How big a subdivision can you handle?',         a: 'From 2-lot minor splits to 500+ lot phased master plans. We scale teams and split sheets by phase.' },
      { q: 'Do you generate legal descriptions?',           a: 'Yes — full metes & bounds legals for each lot, tract, dedication, and easement, in Word and PDF.' },
      { q: 'What turnaround can I expect?',                 a: 'Small plats in 2–3 days; large multi-sheet plats typically 5–10 business days. Rush available.' }
    ]
  },
  {
    slug: 'tree-location-surveys',
    title: 'Tree Location Surveys',
    short: 'CAD drafting of tree location & preservation plans — species, DBH, and canopy mapping from your field data.',
    icon: 'TreePine',
    image: 'https://images.unsplash.com/photo-1473773508845-188df298d2d1?w=1200&h=800&fit=crop',
    features: [
      { title: 'Species & DBH',  desc: 'Tagged from your field notes' },
      { title: 'Canopy Mapping', desc: 'Drip-line & root zone plotting' },
      { title: 'CAD Drafting Only', desc: 'No field work — from your locates' },
      { title: 'Preservation Plans', desc: 'Permit-submittal exhibits' }
    ],
    overview: 'We draft tree location, preservation, and canopy maps from your field-collected data — species tags, DBH, drip-line and protection zones — for permit submittals, tree-removal applications, and environmental review.',
    deliverables: [
      'Tree location with species & DBH labels',
      'Canopy / drip-line / root-zone mapping',
      'Tree protection zone (TPZ) overlays',
      'Removal vs. retention tagging',
      'Mitigation tree-replacement calculations',
      'Permit-submittal exhibit & report'
    ],
    faqs: [
      { q: 'Do you identify trees in the field?',       a: 'No. Field tagging is performed by your arborist or surveyor. We draft from the species, DBH, and locate data you supply.' },
      { q: 'Which permit templates do you support?',    a: 'Municipal tree-removal permit templates, state heritage-tree forms, and HOA/master-association exhibits.' },
      { q: 'Can you calculate mitigation totals?',      a: 'Yes — replacement-tree calculations based on caliper, DBH, or canopy formulas you specify.' },
      { q: 'What CAD deliverables do I get?',           a: 'Layered DWG (one layer per species/condition), sealed-ready PDF, and a mitigation summary table.' }
    ]
  }
];

// Shared content used by ALL survey sub-pages (process, getting started, tech stack).
export const SURVEY_PROCESS = [
  { num: '01', title: 'You Send Field Data',  desc: 'Upload your point files, raw data, field notes, or sketches through our secure portal — no field work on our side.' },
  { num: '02', title: 'Review & Quote',       desc: 'Our team reviews your requirements and provides a detailed CAD drafting quote and timeline within 2 hours.' },
  { num: '03', title: 'CAD Drafting',         desc: 'Our expert technicians draft your survey deliverable in AutoCAD / Civil 3D / Carlson following your templates and standards.' },
  { num: '04', title: 'Quality Check',        desc: 'Every drawing goes through rigorous multi-layer QC against your standards and industry best practices.' },
  { num: '05', title: 'White-Label Delivery', desc: 'Receive your sealed-ready CAD files (DWG, DXF, PDF) — confidential, branded for your firm, ready for your stamp.' }
];

export const SURVEY_GETTING_STARTED = [
  {
    title: 'Preliminary Standard Review',
    items: ['Example .dwg files', 'Example .pdfs', 'Templates (all sizes)', 'Point codes', 'Special notes if any', 'Software preference']
  },
  {
    title: 'Project Completion Documents',
    items: ['Point file (PNEZ, PNZ, ASCII)', 'Geo-referenced ortho mosaics (if available)', 'Site images', 'Prior surveys (if available)', 'Field sketches', 'Plat maps', 'Deeds', 'Legal documents']
  },
  {
    title: 'Project Sharing Platforms',
    items: ['DropBox', 'OneDrive', 'Client project management systems', 'FTPs', 'Email', 'Cloud platforms']
  }
];

export const SURVEY_TECH_STACK = [
  'Autodesk Civil 3D', 'AutoCAD LT', 'Carlson Survey', 'Bentley MicroStation',
  'Leica Cyclone 360', 'Pix4D', 'Global Mapper', 'Trimble Business Center'
];

export const SURVEY_DATA_INPUTS = [
  { title: 'Total Station Data',  desc: 'Raw files from Trimble, Leica, Topcon, Sokkia', icon: 'Crosshair' },
  { title: 'GNSS / RTK Files',    desc: 'Real-time kinematic point exports',              icon: 'Satellite' },
  { title: 'Drone / UAV Imagery', desc: 'Photogrammetry & ortho-mosaic processing',       icon: 'Plane' },
  { title: 'LiDAR Point Clouds',  desc: 'Terrestrial & aerial scan data',                 icon: 'ScanLine' },
  { title: 'PNEZD / ASCII Points', desc: 'Standard ASCII coordinate exports',             icon: 'FileText' },
  { title: 'Field Sketches',      desc: 'Hand sketches & redline mark-ups',               icon: 'Pencil' },
  { title: 'Deeds & Plat Maps',   desc: 'Recorded documents for research',                icon: 'BookOpen' },
  { title: 'Prior Surveys',       desc: 'Existing CAD files & legacy drawings',           icon: 'Folder' },
  { title: 'Raster / PDF Plans',  desc: 'Image-to-CAD vectorization',                     icon: 'Image' },
  { title: 'Field Codes',         desc: 'Custom feature code dictionaries',               icon: 'Code2' }
];

export const SURVEY_INDUSTRIES = [
  { title: 'Land Surveyors & PLS Firms',     desc: 'Overflow CAD drafting support so your licensed surveyors stay focused on field work.',      icon: 'Compass' },
  { title: 'Construction & Infrastructure',  desc: 'Drafting site exhibits and as-builts from contractor and surveyor field data.',             icon: 'HardHat' },
  { title: 'Engineering & Architecture',     desc: 'Survey base maps that drive site planning, drainage, and grading designs.',                 icon: 'Building' },
  { title: 'Government & Municipalities',    desc: 'Recording-ready plats, ROW exhibits, and zoning maps from your field data.',                icon: 'Landmark' },
  { title: 'Environmental & Mining',         desc: 'Tree, wetland, and resource maps drafted from your field-collected points.',                icon: 'TreePine' }
];

export const SURVEY_PROJECTS = [
  {
    category: 'Land Surveying',
    title: 'ALTA Survey for 25-Acre Development',
    challenge: 'Complex boundary with multiple easements and encroachments requiring Table A items.',
    result: 'Delivered comprehensive ALTA/NSPS survey in 48 hours, identifying 3 critical encroachments.',
    metrics: [{ v: '48 hrs', l: 'Turnaround' }, { v: '99.8%', l: 'Accuracy' }, { v: '$15K', l: 'Cost Saved' }]
  },
  {
    category: 'Land Development',
    title: '150-Lot Residential Subdivision',
    challenge: 'Full subdivision design with stormwater management for a tight municipal deadline.',
    result: 'Complete permit-ready plans delivered 5 days early. Approved on first municipal review.',
    metrics: [{ v: '150', l: 'Lots Designed' }, { v: '1st Try', l: 'Permit Pass' }, { v: '2 Weeks', l: 'Time Saved' }]
  }
];
